import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import axios from 'axios';
import {
    Box, Typography, Paper, CircularProgress, IconButton,
    Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
    FormControlLabel, Switch, Chip, Slider, Divider, Button,
    Alert
} from '@mui/material';
import {
    Fullscreen as FullscreenIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { transformToBTC } from '../../utils/helpers';
import ExplorerLayout from '../Components/ExplorerLayout.component';
import { API_BASE_URL } from '../../utils/constants';

const API_BASE_URL_1 = API_BASE_URL + `/explorer`;

const NODE_COLORS = { user: '#81C784', address: '#64B5F6' };
const HIGHLIGHT_STROKE_COLOR = '#FFD700';
const DIM_OPACITY = 0.2;

const shortenLabel = (label, startChars = 8, endChars = 8) => {
    if (typeof label !== 'string' || label.length <= startChars + endChars + 3) return label;
    return `${ label.substring(0, startChars) }...${ label.substring(label.length - endChars) }`;
};
const isCurrentTransactionNode = (nodeId, transactions, txId) => {
    return transactions.some(tx => tx.txId === txId && (tx.user === nodeId || tx.withdrawAddress === nodeId || (tx.senderAddresses && tx.senderAddresses.includes(nodeId))));
};
const getNodeColor = (node) => NODE_COLORS[node.type] || '#666';
const getNodeRadius = (node, transactions, txId) => isCurrentTransactionNode(node.id, transactions, txId) ? 20 : 15;

const handleCopyToClipboard = (text) => {
    if (text && text !== 'N/A') {
        navigator.clipboard.writeText(text);
    }
};

export default function ExplorerTransactionPage() {
    const { t } = useTranslation();
    const { txId } = useParams();
    const navigate = useNavigate();

    const svgRef = useRef();
    const containerRef = useRef();
    const simulationRef = useRef();
    const expandedNodes = useRef(new Set());
    const pinnedPositions = useRef(new Map());

    const [loading, setLoading] = useState(true);
    const [graphData, setGraphData] = useState({ nodes: [], links: [], transactions: [] });
    const [highlightedNodeIds, setHighlightedNodeIds] = useState(new Set());
    const [highlightedLinkIds, setHighlightedLinkIds] = useState(new Set());
    const [hoveredTableLinkID, setHoveredTableLinkID] = useState(null);
    const [showCumulative, setShowCumulative] = useState(true);

    const [windowSize, setWindowSize] = useState(50);
    const [neighborPageSize, setNeighborPageSize] = useState(50);
    const [currentWindow, setCurrentWindow] = useState(0);
    const [selectedNodeTypes, setSelectedNodeTypes] = useState(new Set(['user', 'address']));

    const explorationPlan = useMemo(() => {
        if (!graphData?.nodes?.length) return [];
        const plan = [];
        const filteredNodes = graphData.nodes.filter(node => selectedNodeTypes.has(node.type));
        const neighborsOf = new Map();
        graphData.nodes.forEach(n => neighborsOf.set(n.id, []));
        graphData.links.forEach(link => {
            const sourceId = link.source.id || link.source; const targetId = link.target.id || link.target;
            neighborsOf.get(sourceId)?.push(targetId); neighborsOf.get(targetId)?.push(sourceId);
        });
        const processedAsHub = new Set();
        for (const node of filteredNodes) {
            const neighbors = Array.from(new Set(neighborsOf.get(node.id) || []));
            if (neighbors.length > neighborPageSize) {
                processedAsHub.add(node.id);
                for (let i = 0; i < neighbors.length; i += neighborPageSize) {
                    const neighborSlice = neighbors.slice(i, i + neighborPageSize);
                    const nodeIdsInView = new Set([node.id, ...neighborSlice]);
                    plan.push({ title: `${ shortenLabel(node.id, 6, 6) } (Neighbors ${ i + 1 }-${ i + neighborSlice.length })`, nodeIds: nodeIdsInView });
                }
            }
        }
        const remainingNodes = filteredNodes.filter(n => !processedAsHub.has(n.id));
        for (let i = 0; i < remainingNodes.length; i += windowSize) {
            const nodeSlice = remainingNodes.slice(i, i + windowSize);
            const nodeIdsInView = new Set(nodeSlice.map(n => n.id));
            nodeSlice.forEach(n => { (neighborsOf.get(n.id) || []).forEach(neighborId => nodeIdsInView.add(neighborId)); });
            const endRange = Math.min(i + windowSize, remainingNodes.length);
            plan.push({ title: `Nodes ${ i + 1 }-${ endRange } & Their Neighbors`, nodeIds: nodeIdsInView });
        }
        return plan;
    }, [graphData.nodes, graphData.links, selectedNodeTypes, windowSize, neighborPageSize]);

    const windowedData = useMemo(() => {
        const totalWindows = explorationPlan.length;
        if (totalWindows === 0) return { currentNodes: [], currentLinks: [], totalWindows: 0, title: 'No Data', stats: { total: 0, showing: 0, window: 0, links: 0 } };
        const currentPlanItem = explorationPlan[Math.min(currentWindow, totalWindows - 1)];
        const { nodeIds, title } = currentPlanItem;
        const allNodesById = new Map(graphData.nodes.map(node => [node.id, node]));
        const currentNodes = Array.from(nodeIds).map(id => allNodesById.get(id)).filter(Boolean);
        const currentLinks = graphData.links.filter(link => nodeIds.has(link.source.id || link.source) && nodeIds.has(link.target.id || link.target));
        const totalFilteredNodes = graphData.nodes.filter(node => selectedNodeTypes.has(node.type)).length;
        return { currentNodes, currentLinks, totalWindows, title, stats: { total: totalFilteredNodes, showing: currentNodes.length, window: currentWindow + 1, links: currentLinks.length } };
    }, [graphData.nodes, graphData.links, explorationPlan, currentWindow, selectedNodeTypes]);

    const linksToRender = useMemo(() => {
        const { currentLinks } = windowedData;
        if (showCumulative) {
            const cumulativeMap = new Map();
            currentLinks.forEach(link => {
                const sourceId = link.source.id || link.source; const targetId = link.target.id || link.target;
                const key = `${ sourceId }-${ targetId }`;
                const amountMatch = link.label.match(/(\d+(\.\d+)?)/);
                const amount = amountMatch ? parseFloat(amountMatch[1]) : 0;
                if (cumulativeMap.has(key)) {
                    cumulativeMap.get(key).totalAmount += amount;
                } else {
                    cumulativeMap.set(key, { source: link.source, target: link.target, totalAmount: amount, id: `cumulative-${ key }` });
                }
            });
            return Array.from(cumulativeMap.values());
        } else {
            const linkGroups = d3.group(currentLinks, d => [d.source.id || d.source, d.target.id || d.target].sort().join('-'));
            linkGroups.forEach(group => {
                const groupSize = group.length;
                if (groupSize > 1) {
                    group.forEach((link, i) => { link.arc = (i - (groupSize - 1) / 2); });
                } else {
                    group[0].arc = 0;
                }
            });
            return currentLinks;
        }
    }, [windowedData.currentLinks, showCumulative]);

    const graphLinksSummary = useMemo(() => {
        const allNodesById = new Map(windowedData.currentNodes.map(node => [node.id, node]));
        const addressSummaryMap = new Map();

        windowedData.currentLinks.forEach(link => {
            const sourceId = link.source.id || link.source;
            const targetId = link.target.id || link.target;
            const sourceNode = allNodesById.get(sourceId);
            const targetNode = allNodesById.get(targetId);
            const amountMatch = link.label.match(/(\d+(\.\d+)?)/);
            const amount = amountMatch ? parseFloat(amountMatch[1]) : 0;
            const typeMatch = link.label.match(/(Deposited|Withdrew)/i);
            const type = typeMatch ? typeMatch[1] : 'Transfer';

            if (targetNode?.type === 'address') {
                if (!addressSummaryMap.has(targetId)) addressSummaryMap.set(targetId, { addressId: targetId, totalInflow: 0, totalOutflow: 0, parties: new Set(), types: new Set() });
                const summary = addressSummaryMap.get(targetId);
                summary.totalInflow += amount;
                summary.types.add(type);
                if (sourceNode) summary.parties.add(sourceId);
            }
            if (sourceNode?.type === 'address') {
                if (!addressSummaryMap.has(sourceId)) addressSummaryMap.set(sourceId, { addressId: sourceId, totalInflow: 0, totalOutflow: 0, parties: new Set(), types: new Set() });
                const summary = addressSummaryMap.get(sourceId);
                summary.totalOutflow += amount;
                summary.types.add(type);
                if (targetNode) summary.parties.add(targetId);
            }
        });

        return Array.from(addressSummaryMap.values()).map(summary => ({
            id: summary.addressId,
            address: summary.addressId,
            parties: Array.from(summary.parties),
            types: Array.from(summary.types).join(', '),
            inflow: transformToBTC(summary.totalInflow),
            outflow: transformToBTC(summary.totalOutflow)
        }));
    }, [windowedData.currentLinks, windowedData.currentNodes]);


    const handleTableRowHover = useCallback((summaryItem) => {
        if (!summaryItem) return;
        setHoveredTableLinkID(summaryItem.id);
        const newHighlightedNodes = new Set([summaryItem.address, ...summaryItem.parties]);
        const newHighlightedLinks = new Set();
        linksToRender.forEach(link => {
            const sourceId = link.source.id || link.source;
            const targetId = link.target.id || link.target;
            if (sourceId === summaryItem.address || targetId === summaryItem.address) {
                newHighlightedLinks.add(link.id);
            }
        });
        setHighlightedNodeIds(newHighlightedNodes);
        setHighlightedLinkIds(newHighlightedLinks);
    }, [linksToRender]);

    const handleTableRowLeave = useCallback(() => {
        setHoveredTableLinkID(null);
        setHighlightedNodeIds(new Set());
        setHighlightedLinkIds(new Set());
    }, []);

    const navigateWindow = useCallback((direction) => { setCurrentWindow(prev => { switch (direction) { case 'first': return 0; case 'prev': return Math.max(0, prev - 1); case 'next': return Math.min(windowedData.totalWindows - 1, prev + 1); case 'last': return windowedData.totalWindows - 1; default: return prev; } }); }, [windowedData.totalWindows]);
    useEffect(() => { setCurrentWindow(0); }, [graphData, windowSize, neighborPageSize, selectedNodeTypes]);
    const toggleNodeType = useCallback((type) => { setSelectedNodeTypes(prev => { const newSet = new Set(prev); if (newSet.has(type)) newSet.delete(type); else newSet.add(type); return newSet; }); }, []);
    const resetLayout = () => { pinnedPositions.current.clear(); windowedData.currentNodes.forEach(node => { node.fx = null; node.fy = null; }); if (simulationRef.current) simulationRef.current.alpha(1).restart(); };

    const mergeAndRedraw = useCallback((data) => {
        setGraphData(prevGraph => {
            const getLinkSignature = (link) => `${ link.source.id || link.source }-${ link.target.id || link.target }-${ link.label }`;
            const existingNodeIds = new Set(prevGraph.nodes.map(n => n.id));
            const existingLinkSignatures = new Set(prevGraph.links.map(getLinkSignature));
            const newNodes = data.nodes.filter(n => !existingNodeIds.has(n.id));
            const newLinks = data.links.filter(link => !existingLinkSignatures.has(getLinkSignature(link))).map((link, idx) => ({ ...link, id: `link-${ prevGraph.links.length + idx }` }));
            if (newNodes.length === 0 && newLinks.length === 0) return prevGraph;
            return { ...prevGraph, nodes: [...prevGraph.nodes, ...newNodes], links: [...prevGraph.links, ...newLinks] };
        });
    }, []);

    const drawGraph = useCallback(() => {
        if (!svgRef.current || !windowedData.currentNodes.length) return;
        const { currentNodes } = windowedData;
        const svg = d3.select(svgRef.current);
        const { width, height } = svgRef.current.getBoundingClientRect();
        let zoomGroup = svg.select('.zoom-group');
        if (zoomGroup.empty()) {
            svg.selectAll('*').remove();
            zoomGroup = svg.append('g').attr('class', 'zoom-group');
            const zoom = d3.zoom().on('zoom', (event) => zoomGroup.attr('transform', event.transform));
            svg.call(zoom);
        }
        let defs = zoomGroup.select('defs');
        if (defs.empty()) defs = zoomGroup.append('defs');
        defs.selectAll('marker').data([{ id: 'arrowhead-default', color: '#888' }, { id: 'arrowhead-highlight', color: HIGHLIGHT_STROKE_COLOR }]).join(enter => enter.append('marker').attr('id', d => d.id).attr('viewBox', '-0 -5 10 10').attr('refX', 23).attr('refY', 0).attr('orient', 'auto').attr('markerWidth', 6).attr('markerHeight', 6).append('path').attr('d', 'M0,-5L10,0L0,5')).select('path').attr('fill', d => d.color);

        currentNodes.forEach(node => { if (pinnedPositions.current.has(node.id)) { const pos = pinnedPositions.current.get(node.id); node.fx = pos.fx; node.fy = pos.fy; } });
        if (!simulationRef.current) {
            simulationRef.current = d3.forceSimulation().force('link', d3.forceLink().id(d => d.id).distance(150)).force('charge', d3.forceManyBody().strength(-500)).force('center', d3.forceCenter(width / 2, height / 2));
        }
        const simulation = simulationRef.current;
        simulation.nodes(currentNodes).force('link').links(linksToRender);
        if (currentNodes.length > 0) simulation.alpha(0.3).restart();

        const linkGroup = zoomGroup.selectAll('.link-group').data(linksToRender, d => d.id);
        linkGroup.exit().remove();
        const linkGroupEnter = linkGroup.enter().append('g').attr('class', 'link-group');
        linkGroupEnter.append('path').attr('class', 'link-line').attr('fill', 'none');
        linkGroupEnter.append('text').attr('class', 'link-label').attr('font-size', '10px').attr('text-anchor', 'middle').attr('dy', -5).style('paint-order', 'stroke fill').style('stroke', '#222').style('stroke-width', '3px');
        const mergedLinkGroup = linkGroupEnter.merge(linkGroup).attr('opacity', d => highlightedLinkIds.has(d.id) ? 1 : (highlightedNodeIds.size > 0 ? DIM_OPACITY : 1));

        mergedLinkGroup.select('.link-line').attr('stroke', d => highlightedLinkIds.has(d.id) ? HIGHLIGHT_STROKE_COLOR : '#888').attr('stroke-width', d => highlightedLinkIds.has(d.id) ? 2.5 : 1.5).attr('marker-end', d => highlightedLinkIds.has(d.id) ? 'url(#arrowhead-highlight)' : 'url(#arrowhead-default)');
        mergedLinkGroup.select('.link-label').attr('fill', d => highlightedLinkIds.has(d.id) ? HIGHLIGHT_STROKE_COLOR : '#FFFFFF').text(d => {
            const amount = d.totalAmount ?? (d.label?.match(/(\d+(\.\d+)?)/) ? parseFloat(d.label.match(/(\d+(\.\d+)?)/)[1]) : null);
            return amount !== null ? transformToBTC(amount) : '';
        });

        const node = zoomGroup.selectAll('.node').data(currentNodes, d => d.id);
        node.exit().remove();
        const nodeEnter = node.enter().append('g').attr('class', 'node').style('cursor', 'pointer');
        nodeEnter.append('circle');
        nodeEnter.append('text').attr('class', 'node-label').attr('font-size', 10).attr('pointer-events', 'none').attr('text-anchor', 'middle');
        const mergedNode = nodeEnter.merge(node).attr('opacity', d => highlightedNodeIds.size > 0 && !highlightedNodeIds.has(d.id) ? DIM_OPACITY : 1);

        mergedNode.select('circle').attr('r', d => getNodeRadius(d, graphData.transactions, txId)).attr('fill', d => getNodeColor(d)).attr('stroke', d => highlightedNodeIds.has(d.id) ? HIGHLIGHT_STROKE_COLOR : (isCurrentTransactionNode(d.id, graphData.transactions, txId) ? '#FF5722' : '#555')).attr('stroke-width', d => highlightedNodeIds.has(d.id) || isCurrentTransactionNode(d.id, graphData.transactions, txId) ? 3 : 1.5);
        mergedNode.select('.node-label').attr('dy', d => getNodeRadius(d, graphData.transactions, txId) + 12).text(d => shortenLabel(d.id)).attr('fill', '#FFFFFF');

        mergedNode.on('click', async (event, d) => {
            if (expandedNodes.current.has(d.id)) return;
            expandedNodes.current.add(d.id);
            setLoading(true);
            try { const res = await axios.get(`${ API_BASE_URL_1 }/explore-node/${ d.type }/${ d.id }`); mergeAndRedraw(res.data); }
            catch (err) { console.error('Error exploring node:', err); expandedNodes.current.delete(d.id); }
            finally { setLoading(false); }
        })
            .on('mouseover', function (event, d) {
                const newHighlightedNodes = new Set([d.id]);
                const newHighlightedLinks = new Set();
                linksToRender.forEach(l => { if ((l.source.id || l.source) === d.id || (l.target.id || l.target) === d.id) { newHighlightedLinks.add(l.id); newHighlightedNodes.add(l.source.id || l.source); newHighlightedNodes.add(l.target.id || l.target); } });
                setHighlightedNodeIds(newHighlightedNodes);
                setHighlightedLinkIds(newHighlightedLinks);
            })
            .on('mouseout', function () { setHighlightedNodeIds(new Set()); setHighlightedLinkIds(new Set()); })
            .call(d3.drag().on('start', (event, d) => { event.sourceEvent.stopPropagation(); if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; }).on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; }).on('end', (event, d) => { if (!event.active) simulation.alphaTarget(0); pinnedPositions.current.set(d.id, { fx: d.x, fy: d.y }); }));

        simulation.on('tick', () => {
            mergedLinkGroup.select('.link-line').attr('d', d => {
                const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y;
                if (d.arc === 0 || !d.arc) return `M${ d.source.x },${ d.source.y }L${ d.target.x },${ d.target.y }`;
                const midX = (d.source.x + d.target.x) / 2, midY = (d.source.y + d.target.y) / 2;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return `M${ d.source.x },${ d.source.y }`;
                const offsetX = -dy / length * d.arc * 20, offsetY = dx / length * d.arc * 20;
                return `M${ d.source.x },${ d.source.y }Q${ midX + offsetX },${ midY + offsetY } ${ d.target.x },${ d.target.y }`;
            });
            mergedLinkGroup.select('.link-label').attr('transform', d => {
                if (d.arc === 0 || !d.arc) return `translate(${ (d.source.x + d.target.x) / 2 }, ${ (d.source.y + d.target.y) / 2 })`;
                const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y;
                const midX = (d.source.x + d.target.x) / 2, midY = (d.source.y + d.target.y) / 2;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return `translate(${ midX }, ${ midY })`;
                const offsetX = -dy / length * d.arc * 20, offsetY = dx / length * d.arc * 20;
                return `translate(${ midX + offsetX }, ${ midY + offsetY })`;
            });
            mergedNode.attr('transform', d => `translate(${ d.x },${ d.y })`);
        });
    }, [windowedData, linksToRender, mergeAndRedraw, graphData.transactions, txId, highlightedNodeIds, highlightedLinkIds]);

    useEffect(() => {
        const fetchInitialGraph = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${ API_BASE_URL_1 }/connections/${ txId }`);
                expandedNodes.current.clear();
                const linksWithId = res.data.links.map((link, idx) => ({ ...link, id: `link-${ idx }` }));
                setGraphData({ ...res.data, links: linksWithId });
            } catch (err) { console.error('Error fetching initial graph:', err); }
            finally { setLoading(false); }
        };
        fetchInitialGraph();
    }, [txId]);

    useEffect(() => { drawGraph(); }, [linksToRender, drawGraph]);

    const toggleFullscreen = () => { if (!document.fullscreenElement) containerRef.current.requestFullscreen(); else document.exitFullscreen(); };

    return (
        <Box p={3}>
            <Paper elevation={3} sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="text.secondary">{`${ t('transactionId') }: ${ txId }`}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel control={<Switch checked={showCumulative} onChange={(e) => setShowCumulative(e.target.checked)} />} label={t('explorer.cumulativeGraphLinks')} />
                    <Button variant="outlined" size="small" onClick={resetLayout} startIcon={<RestartAltIcon />}>{t('explorer.resetLayout')}</Button>
                    <Tooltip title={t('toggleFullscreen')}><IconButton onClick={toggleFullscreen} color="primary"><FullscreenIcon /></IconButton></Tooltip>
                </Box>
            </Paper>
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('explorer.explorerProTip')}
            </Alert>
            <Box ref={containerRef} sx={{ border: '1px solid #444', borderRadius: '8px', height: 650, position: 'relative', bgcolor: '#222' }}>
                {loading && <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}><CircularProgress /></Box>}
                <svg ref={svgRef} width="100%" height="100%" style={{ display: loading ? 'none' : 'block' }} />
            </Box>
            <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, mt: 1, boxShadow: 1 }}>
                <IconButton size="small" onClick={() => navigateWindow('first')} disabled={currentWindow === 0}><FirstPageIcon /></IconButton>
                <IconButton size="small" onClick={() => navigateWindow('prev')} disabled={currentWindow === 0}><NavigateBeforeIcon /></IconButton>
                <Box sx={{ minWidth: 200, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{windowedData.title}</Typography>
                    <Typography variant="caption" display="block">{t('explorer.window')}: {windowedData.stats.window || 1}/{windowedData.totalWindows || 1}</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigateWindow('next')} disabled={currentWindow >= windowedData.totalWindows - 1}><NavigateNextIcon /></IconButton>
                <IconButton size="small" onClick={() => navigateWindow('last')} disabled={currentWindow >= windowedData.totalWindows - 1}><LastPageIcon /></IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>{['user', 'address'].map(type => (<Chip key={type} label={type === 'user' ? t('user.user') : t('explorer.address')} size="small" variant={selectedNodeTypes.has(type) ? 'filled' : 'outlined'} onClick={() => toggleNodeType(type)} sx={{ bgcolor: selectedNodeTypes.has(type) ? NODE_COLORS[type] : 'transparent', color: selectedNodeTypes.has(type) ? 'white' : 'text.primary' }} />))}</Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography variant="body2" color="text.secondary">{t('explorer.nodes')}: {windowedData.stats.showing}/{windowedData.stats.total}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>{t('explorer.links')}: {linksToRender.length}</Typography>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="body2" color="text.secondary">{t('explorer.hubSize')}:</Typography><Slider value={neighborPageSize} onChange={(_, value) => setNeighborPageSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" /></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}><Typography variant="body2" color="text.secondary">{t('explorer.groupSize')}:</Typography><Slider value={windowSize} onChange={(_, value) => setWindowSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" /></Box>
            </Paper>

            <Typography variant="h5" mt={4} mb={2} sx={{ fontWeight: 'bold' }}>
                {t('explorer.addressSummary')} ({t('explorer.visibleLinks')})
            </Typography>
            <Paper elevation={3} sx={{ bgcolor: 'background.paper', mb: 4 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('explorer.address')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('explorer.transactionTypes')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('explorer.interactingUsers')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }} align="right">{t('explorer.totalInflow')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }} align="right">{t('explorer.totalOutflow')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {graphLinksSummary.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('explorer.noAddressTransactionsVisible')}</TableCell>
                            </TableRow>
                        ) : (
                            graphLinksSummary.map(item => (
                                <TableRow
                                    key={item.id}
                                    hover
                                    sx={{ bgcolor: hoveredTableLinkID === item.id ? 'rgba(255, 152, 0, 0.2)' : 'inherit', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.05)' } }}
                                    onMouseEnter={() => handleTableRowHover(item)}
                                    onMouseLeave={handleTableRowLeave}
                                >
                                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                                        <Tooltip title={item.address} placement="top">
                                            <Box component="span"
                                                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                onClick={() => window.open(`/explorer/address/${ item.address }`)}
                                                onContextMenu={e => { e.preventDefault(); handleCopyToClipboard(item.address); }}
                                            >
                                                {shortenLabel(item.address, 10, 8)}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary' }}>{item.types}</TableCell>
                                    <TableCell sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                                        {item.parties.map((partyId, index) => {
                                            const transactionsForUser = windowedData.currentLinks.filter(link => {
                                                const sourceId = link.source.id || link.source;
                                                const targetId = link.target.id || link.target;
                                                return (sourceId === partyId && targetId === item.address) || (sourceId === item.address && targetId === partyId);
                                            });

                                            const tooltipContent = (
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                                                        {t('explorer.transactionsWith')} {shortenLabel(partyId, 6, 6)}:
                                                    </Typography>
                                                    {transactionsForUser.map((tx, txIndex) => {
                                                        const amountMatch = tx.label.match(/(\d+(\.\d+)?)/);
                                                        const amount = amountMatch ? transformToBTC(parseFloat(amountMatch[1])) : '';
                                                        const typeMatch = tx.label.match(/(Deposited|Withdrew)/i);
                                                        const type = typeMatch ? typeMatch[1] : 'Transfer';
                                                        return (
                                                            <Typography key={tx.id || txIndex} variant="caption" display="block" sx={{ color: 'inherit', mt: 0.5 }}>
                                                                • {type}: {amount}
                                                            </Typography>
                                                        );
                                                    })}
                                                </Box>
                                            );

                                            return (
                                                <React.Fragment key={partyId}>
                                                    <Tooltip title={tooltipContent} placement="top">
                                                        <span>
                                                            <Button
                                                                size="small" variant="text"
                                                                onClick={(e) => { e.stopPropagation(); window.open(`/explorer/user/${ partyId }`); }}
                                                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontFamily: 'monospace', fontWeight: 'bold' }}
                                                            >
                                                                {shortenLabel(partyId, 4, 4)}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    {index < item.parties.length - 1 && ', '}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main' }}>{item.inflow}</TableCell>
                                    <TableCell align="right" sx={{ color: 'error.main' }}>{item.outflow}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Typography variant="h5" mt={4} mb={2} sx={{ fontWeight: 'bold' }}>
                {t('explorer.allTransactionDetails')} ({t('explorer.rawData')})
            </Typography>
            <Paper elevation={3} sx={{ bgcolor: 'background.paper' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('table.type')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('transactionId')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('user.user')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('explorer.address')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }} align="right">{t('trading.amount')} (BTC)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>{t('table.time')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {graphData.transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>{t('explorer.noRawTransactions')}</TableCell>
                            </TableRow>
                        ) : (
                            graphData.transactions.map(tx => {
                                const address = tx.withdrawAddress || (tx.senderAddresses && tx.senderAddresses[0]) || 'N/A';
                                return (
                                    <TableRow key={tx.txId} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.05)' } }}>
                                        <TableCell sx={{ color: 'text.primary' }}>{tx.withdrawAddress ? t('explorer.withdrawal') : t('explorer.deposit')}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                                            <Button
                                                size="small" variant="text"
                                                onClick={() => window.open(`/explorer/transaction/${ tx.txId }`)}
                                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 'bold' }}
                                            >
                                                {shortenLabel(tx.txId, 6, 4)}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small" variant="text"
                                                onClick={() => window.open(`/explorer/user/${ tx.user }`)}
                                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 'bold' }}
                                            >
                                                {shortenLabel(tx.user, 6, 4)}
                                            </Button>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                                            <Tooltip title={address} placement="top">
                                                <Box component="span"
                                                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                    onClick={() => window.open(`/explorer/address/${ address }`)}
                                                    onContextMenu={e => { e.preventDefault(); handleCopyToClipboard(address); }}
                                                >
                                                    {shortenLabel(address, 6, 4)}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.primary' }}>{transformToBTC(tx.amount)}</TableCell>
                                        <TableCell sx={{ color: 'text.primary' }}>{new Date(tx.time).toLocaleString()}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}